import { Link, type ErrorComponentProps } from "@tanstack/react-router";
import { RivetsApiError } from "@rivets/core";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";

export function isNotFound(error: unknown): boolean {
  return error instanceof RivetsApiError && error.status === 404;
}

/** What went wrong, in the API's words when it answered at all. A thrown
 * `TypeError` from `fetch` is the only case that is really the connection. */
export function errorHint(error: unknown): string {
  if (error instanceof RivetsApiError) return error.message;
  return "Check your connection and try again.";
}

/** A detail page whose id the API does not know: deleted, or a bad link.
 * Retrying would only ask again, so the way out is back to the list. */
export function AdminNotFound({ title, hint, back }: { title: string; hint: string; back: { to: string; label: string } }) {
  return (
    <div className="page admin-page">
      <EmptyState
        title={title}
        hint={hint}
        action={
          <Link className="btn btn-sm" to={back.to}>
            {back.label}
          </Link>
        }
      />
    </div>
  );
}

/**
 * Every admin page's `errorComponent`. It is set on the pages rather than on
 * `/admin` itself so it renders inside `AdminShell`'s outlet: the sidebar
 * stays, and the operator can go somewhere else instead of reloading.
 */
export function AdminRouteError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="page admin-page">
      <ErrorState
        title="This page failed to render"
        hint={error instanceof Error ? error.message : String(error)}
        onRetry={reset}
      />
    </div>
  );
}
